import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Market} from "../../../storage/models/market.model";
import {BehaviorSubject, Observable, Subject, Subscription} from "rxjs";
import {MarketService} from "../../../providers/services/market.service";
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {IGenericKeyValue, IRRUItem} from "../../../storage/interfaces/generic.interface";
import {ReferencesService} from "../../../providers/services/references.service";
import {debounceTime, filter, map, skip, startWith, take, takeUntil, tap} from "rxjs/operators";
import {LoaderService} from "../../../providers/services/loader.service";
import {StringUtils} from "../../../providers/utils/string-utils";
import {MatSelect} from "@angular/material/select";
import {IQuestionnaire, IQuestionnaireForApi} from "../../../storage/interfaces/questionnaire.interface";
import {QuestionnaireService} from "../../../providers/services/questionnaire.service";
import {IApiResponse} from "../../../storage/interfaces/api.interface";
import {
  CONSTANTS,
  DMDS_QUANTITY_UNIT_LIST,
  PRODUCTS_LIST,
  VALIDATOR_MAX_DMDS_QTY,
  VALIDATOR_MAX_REACTOR,
  VALIDATOR_MIN_DMDS_QTY,
  VALIDATOR_MIN_REACTOR,
  CATALYST_WEIGHT_UNIT_LIST,
  VALIDATOR_MIN_CATALYST_WEIGHT,
  VALIDATOR_MAX_CATALYST_WEIGHT,
} from "../../../constants";
import {GenericKeyValueFormGroup} from "../../../storage/models/generic-key-value-form-group";
import {DateService} from "../../../providers/services/date.service";
import {MissionsService} from "../../../providers/services/missions.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Router} from "@angular/router";

export function RequireMatch(control: AbstractControl) {
  const selection: any = control.value;
  if (typeof selection === "string") {
    return {incorrect: true};
  }
  return null;
}

@Component({
    selector: 'app-mission-create',
    templateUrl: './mission-create.component.html',
    styleUrls: ['./mission-create.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MissionCreateComponent implements OnInit, OnDestroy {
  selectedMarket: Market;
  selectedMarketSubscription: Subscription;

  form!: UntypedFormGroup;
  rruFC = new UntypedFormControl('', [Validators.required, RequireMatch]);
  productFC = new UntypedFormControl('', Validators.required);
  quantityFC = new UntypedFormControl('', Validators.required);
  quantityUnitFG = new GenericKeyValueFormGroup(null);
  catalystWeightFC = new UntypedFormControl('');
  catalystWeightUnitFG = new GenericKeyValueFormGroup(null);
  nbReactorFC = new UntypedFormControl('', [Validators.required]);
  sulfidingDateFC = new UntypedFormControl('', Validators.required);
  questionnaireIdFC = new UntypedFormControl('', Validators.required);


  showHighlight: boolean = false;
  private _rruListSubject = new BehaviorSubject<IRRUItem[]>([]);
  rruList$ = this._rruListSubject.asObservable();
  filteredRruList$!: Observable<IRRUItem[]>;
  private _questionnaireDataListSubject = new BehaviorSubject<IQuestionnaire<string>[]>([]);
  questionnaireDataList$ = this._questionnaireDataListSubject.asObservable();
  private _selectedDataSubject = new BehaviorSubject<IQuestionnaire<string>>({} as IQuestionnaire<string>);
  selectedData$ = this._selectedDataSubject.asObservable();
  private _componentDestroyed$: Subject<boolean> = new Subject();
  private _productListSubject = new BehaviorSubject<IGenericKeyValue[]>(PRODUCTS_LIST);
  productList$ = this._productListSubject.asObservable();
  private _creatingSubject = new BehaviorSubject<boolean>(false);
  creating$= this._creatingSubject.asObservable();

  @ViewChild('dataSelector', {static: false}) _dataSelector: MatSelect;
  VALIDATOR_MIN_DMDS_QTY = VALIDATOR_MIN_DMDS_QTY;
  VALIDATOR_MAX_DMDS_QTY = VALIDATOR_MAX_DMDS_QTY;
  DMDS_QUANTITY_UNIT_LIST = DMDS_QUANTITY_UNIT_LIST;
  VALIDATOR_MIN_REACTOR = VALIDATOR_MIN_REACTOR;
  VALIDATOR_MAX_REACTOR = VALIDATOR_MAX_REACTOR;
  CATALYST_WEIGHT_UNIT_LIST = CATALYST_WEIGHT_UNIT_LIST;
  VALIDATOR_MIN_CATALYST_WEIGHT = VALIDATOR_MIN_CATALYST_WEIGHT;
  VALIDATOR_MAX_CATALYST_WEIGHT = VALIDATOR_MAX_CATALYST_WEIGHT;
  constructor(private _marketService: MarketService,
              private _loaderService: LoaderService,
              private _referenceService: ReferencesService,
              private _questionnaireService: QuestionnaireService,
              private _fb: UntypedFormBuilder,
              private _dateService: DateService,
              private _missionService: MissionsService,
              private _snackBar: MatSnackBar,
              private _router: Router
  ) {
  }

  ngOnInit(): void {
    this._loaderService.show();

    this.form = this._fb.group({
      rru: this.rruFC,
      questionnaireId: this.questionnaireIdFC,
      product: this.productFC,
      quantity: this.quantityFC,
      quantityUnit: this.quantityUnitFG,
      catalystWeight: this.catalystWeightFC,
      catalystWeightUnit: this.catalystWeightUnitFG,
      nbReactor: this.nbReactorFC,
      sulfidingDate: this.sulfidingDateFC
    });

    this.selectedMarketSubscription = this._marketService.selectedMarket$.subscribe(async selectedMarket => {
      if (selectedMarket) {
        this.form.reset();
        this.selectedMarket = selectedMarket;
        this.rruFC.setValue('', {emitEvent: true});
      }
    });

    this._referenceService.getRRUListByMarket()
      .pipe(
        take(1),
        tap(() => this._loaderService.hide()))
      .subscribe(resp => {
        this._rruListSubject.next(resp.data);
      })

    this.filteredRruList$ = this.rruFC.valueChanges.pipe(
      startWith(''),
      map(value => {
        if (StringUtils.isBlankOrNull(value)) {
          this.showHighlight = false;
          const allRRU = this._rruListSubject.getValue();
          return !!this.selectedMarket?.marketname ? allRRU.filter(v => v.market === this.selectedMarket.marketname) : allRRU;
        } else {
          this.showHighlight = true;
          return this._filter(value || '');
        }
      })
    );

    this.rruFC.valueChanges
      .pipe(takeUntil(this._componentDestroyed$),
        debounceTime(250),
      )
      .subscribe(v => {
        if (typeof v === 'object' && v !== null) {
          this._loadQuestionnairesList();
          this.questionnaireIdFC.enable({emitEvent: true});
        } else {
          this.questionnaireIdFC.disable({emitEvent: true});
        }
        this._clearSelectedData();
      });

    this.questionnaireIdFC.valueChanges
      .pipe(takeUntil(this._componentDestroyed$),
        skip(1),
      )
      .subscribe((v) => {
        let newValue: IQuestionnaire<string> = {} as IQuestionnaire<string>;
        if (!v) {
          newValue = null;
        } else if (v === -1) {
          newValue = {
            preparationPk: -1,
            unitId: this.rruFC.value.key,
            reactors: [{name: 'Reactor 1', bedNumber: 1}]
          } as IQuestionnaire<string>;
        } else {
          const liste = this._questionnaireDataListSubject.getValue();
          const foundItem = liste.find(i => i.preparationPk === v);
          newValue = !!foundItem ? foundItem : {} as IQuestionnaire<string>;
        }
        this._selectedDataSubject.next(newValue);
        this._propagateData();
      });
  }

  ngOnDestroy(): void {
    this._componentDestroyed$.next(void 0);
  }

  displayFnRRU(rru?: IGenericKeyValue): string {
    return rru?.value || '';
  }

  compareById(o1: IGenericKeyValue, o2: IGenericKeyValue): boolean {
    return (o1 && o2 && o1['id'] === o2['id']) || (!o1 && !o2 && o1 === o2);
  }

  compare(o1: IGenericKeyValue, o2: IGenericKeyValue): boolean {
    return (o1 == o2);
  }
  
  onResetSelectionData(): void {
    this.questionnaireIdFC.setValue('', {emitEvent: true});
    this._dataSelector.open();
  }

  onCreateManuallyClickHandler(): void {
    this.questionnaireIdFC.setValue(-1, {emitEvent: true});
  }

  onValidateNewMissionClickHandler(): void {
    this._creatingSubject.next(true);
    const selectedData = this._selectedDataSubject.getValue();
    const newMission: IQuestionnaireForApi = {
      ...selectedData,
      productPk: this.productFC.value,
      quantity: this.quantityFC.value,
      quantityUnit: this.quantityUnitFG.keyFC.value,
      nbReactor: this.nbReactorFC.value,
      sulfidingDate: this._dateService.convertDateToTimestamp(this.sulfidingDateFC.value),
      market: this.selectedMarket.marketname,
    };

    if (!newMission.totalCatalystQuantity) newMission.totalCatalystQuantity = this.catalystWeightFC.value || 0;
    if (!newMission.totalCatalystQuantityUnit) newMission.totalCatalystQuantityUnit = this.catalystWeightUnitFG.keyFC.value || 'MT';

    this._missionService.createNewMission(newMission)
      .pipe(take(1))
      .subscribe({
      next: (res: IApiResponse<boolean>) => {
        if (res.data) {
          this._snackBar.open('Mission has been successfully created');
          this._router.navigateByUrl(`${CONSTANTS.ROUTES.HOME}/${CONSTANTS.ROUTES.MISSION_SELECTION}`);
        } else {
          this._snackBar.open('Error while creating mission');
        }
        this._creatingSubject.next(false);
      },
      error: (err) => {
        this._snackBar.open('Error while creating mission');
        console.error(err);
        this._creatingSubject.next(false);
      }});
  }

  private _filter(value: string): IRRUItem[] {
    const allRRU = this._rruListSubject.getValue();
    const globalList = !!this.selectedMarket?.marketname ? allRRU.filter(v => v.market === this.selectedMarket.marketname) : allRRU;

    if (!value) {
      return globalList;
    }

    const filterValue = typeof value === 'string' ? value.toLowerCase() : value;
    return globalList.filter(item => item.value.toLowerCase().includes(filterValue));
  }

  private _loadQuestionnairesList(): void {
    const selectedRru = this.rruFC.value;
    // check if rru exists in the list to avoid URL Redirection to Untrusted Site ('Open Redirect') flaw (CWE 601)
    const rruList = this._rruListSubject.getValue();
    const foundRru = rruList.filter(r => r.key === selectedRru.key);

    if (!!selectedRru && foundRru?.length > 0) {
      this._questionnaireService.getQuestionnairesByRRU(selectedRru.key)
        .pipe(
          take(1),
          filter((apiResponse: IApiResponse<IQuestionnaire<string>[]>) => apiResponse.success),
          map((apiResponse: IApiResponse<IQuestionnaire<string>[]>) => apiResponse.data)
        )
        .subscribe((list: IQuestionnaire<string>[]) => {
          this._questionnaireDataListSubject.next(list);
        });
    }
  }

  private _clearSelectedData(): void {
    this.questionnaireIdFC.setValue(null);
  }

  private _propagateData(): void {
    const selectedQuestionnaire = this._selectedDataSubject.getValue();
    if (!!selectedQuestionnaire) {

      if (!selectedQuestionnaire?.preparationPk || selectedQuestionnaire.preparationPk === -1) {
        this.productFC.setValue('');
        this.quantityFC.setValue('');
        this.quantityUnitFG.setValue({key: 'TO', value: 'MT'});
        this.nbReactorFC.setValue(1);
        this.sulfidingDateFC.setValue('');

      } else {
        this.productFC.setValue(selectedQuestionnaire.productPk, {emitEvent: true});
        this.quantityFC.setValue(selectedQuestionnaire.quantity, {emitEvent: true});
        this.quantityUnitFG.setValue({key: selectedQuestionnaire.quantityUnit, value: ''}, {emitEvent: true});
        this.nbReactorFC.setValue(selectedQuestionnaire.nbReactor, {emitEvent: true});
        if (!!selectedQuestionnaire.sulfidingDate) {
          const sulfidingDate = new Date(parseInt(selectedQuestionnaire.sulfidingDate, 10) * 1000).toISOString();
          this.sulfidingDateFC.setValue(sulfidingDate, {emitEvent: true});
        } else {
          this.sulfidingDateFC.setValue('', {emitEvent: true});
        }
      }
    }
  }
}
